import { getConfig } from '../config';

export interface IsSiteCacheInvalidatingResponse {
  isInvalidating: boolean;
}

export interface ClearProductCacheBySKUsRequest {
  skus: string;
}

export class SiteCacheClient {
  constructor(private readonly apiUrl: string) {}

  private getApiBaseUrl(): string {
    return this.apiUrl;
  }

  /**
   * Invalidate site cache
   */
  async invalidateCache(): Promise<void> {
    const response = await fetch(`${this.getApiBaseUrl()}/siteConfig/invalidateCache`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to invalidate cache: ${response.statusText}`);
    }
  }

  /**
   * Check if site cache invalidation is currently running
   */
  async isInvalidating(): Promise<IsSiteCacheInvalidatingResponse> {
    const response = await fetch(`${this.getApiBaseUrl()}/siteConfig/cache/isInvalidating`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Failed to check invalidation status: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Invalidate product cache by publication status
   */
  async invalidateProductsByStatus(publicationStatus?: 'draft'): Promise<void> {
    const url = publicationStatus
      ? `${this.getApiBaseUrl()}/products/invalidateCache?publicationStatus=${publicationStatus}`
      : `${this.getApiBaseUrl()}/products/invalidateCache`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to invalidate products by status: ${response.statusText}`);
    }
  }

  /**
   * Clear product cache by SKUs (works for any publication status)
   */
  async clearProductCacheBySKUs(skus: string): Promise<void> {
    const response = await fetch(`${this.getApiBaseUrl()}/products/clearCache?skus=${encodeURIComponent(skus)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to clear product cache by SKUs: ${response.statusText}`);
    }
  }

  /**
   * Purge Cloudflare cache only
   */
  async purgeCloudflareCache(): Promise<void> {
    const response = await fetch(`${this.getApiBaseUrl()}/siteConfig/invalidateCache/cloudFlare`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': getConfig().apiKey,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to purge Cloudflare cache: ${response.statusText}`);
    }
  }
}
